<template>
  <div>
    <h2 class="section-title">{{ t('openJobs') }}</h2>

    <!-- Filtreler -->
    <div class="filters-side">
      <input v-model="search" :placeholder="t('searchPlaceholder')" />
      <input v-model="langFilter" :placeholder="t('langPlaceholder')" />
      <select v-model="selectedHat">
        <option value="">{{ t('allHatTypes') }}</option>
        <option v-for="hat in hatTurleri" :key="hat.id" :value="hat.name">{{ hat.name }}</option>
      </select>
      <input type="date" v-model="maxDeliveryDate" />
      <select v-model="sortOption">
        <option value="">{{ t('sortNone') }}</option>
        <option value="price-asc">{{ t('priceAsc') }}</option>
        <option value="price-desc">{{ t('priceDesc') }}</option>
        <option value="delivery-nearest">{{ t('deliveryNearest') }}</option>
        <option value="filename-asc">{{ t('filenameAsc') }}</option>
        <option value="filename-desc">{{ t('filenameDesc') }}</option>
      </select>
    </div>

    <!-- Tablo -->
    <table class="job-table">
      <thead>
        <tr>
          <th @click="sortBy('id')" class="sortable">
            {{ t('jobCode') }}
            <span v-if="sortKey === 'id'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
          </th>
          <th @click="sortBy('file_name')" class="sortable">
            {{ t('file') }}
            <span v-if="sortKey === 'file_name'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
          </th>
          <th @click="sortBy('source_lang')" class="sortable">
            {{ t('langs') }}
            <span v-if="sortKey === 'source_lang'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
          </th>
          <th @click="sortBy('specialization')" class="sortable">
            {{ t('specialization') }}
            <span v-if="sortKey === 'specialization'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
          </th>
          <th @click="sortBy('user_name')" class="sortable">
            {{ t('customer') }}
            <span v-if="sortKey === 'user_name'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
          </th>
          <th @click="sortBy('hat_tur_name')" class="sortable">
            {{ t('hatType') }}
            <span v-if="sortKey === 'hat_tur_name'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
          </th>
          <th @click="sortBy('delivery_date')" class="sortable">
            {{ t('deliveryDate') }}
            <span v-if="sortKey === 'delivery_date'">{{ sortDirection === 1 ? '▲' : '▼' }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="job in paginatedJobs"
          :key="job.id"
          :class="{ highlight: isSoon(job.delivery_date) }"
          @click="toggleOffer(job.id)"
          style="cursor: pointer"
        >
          <td>{{ formatJobCode(job.id) }}</td>
          <td>
            <a
              :href="`${API_URL}/download/${job.file_name}`"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
              style="color: #3498db; text-decoration: underline"
            >
              {{ getOriginalFileName(job.file_name) }}
            </a>
          </td>
          <td>{{ job.source_lang }} → {{ job.target_lang }}</td>
          <td>{{ job.specialization || '-' }}</td>
          <td>{{ job.user_name || '-' }} {{ job.user_surname || '' }}</td>

          <td>
            <span v-if="job.source_lang === 'Osmanlıca'">{{ job.hat_tur_name || '-' }}</span>
            <span v-else>-</span>
          </td>
          <td>{{ formatDate(job.delivery_date) }}</td>
          <td><button class="btn small" @click.stop="toggleOffer(job.id)">{{ t('offer') }}</button></td>
        </tr>
      </tbody>
    </table>

    <!-- Popup -->
    <div v-for="job in jobs" :key="'popup-' + job.id">
      <div v-if="offerVisible[job.id]" class="popup-overlay" @click.self="toggleOffer(job.id)">
        <div class="popup-box">
          <h3>{{ t('details') }}</h3>
          <table class="popup-table">
            <tbody>
              <tr><th>{{ t('jobCode') }}</th><td>{{ formatJobCode(job.id) }}</td></tr>
              <tr><th>{{ t('file') }}</th><td>
                <a
                  :href="`${API_URL}/download/${job.file_name}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  @click.stop
                  style="color: #3498db; text-decoration: underline"
                >
                  {{ getOriginalFileName(job.file_name) }}
                </a>
              </td></tr>
              <tr><th>{{ t('desc') }}</th><td>{{ job.description || '-' }}</td></tr>
              <tr><th>{{ t('langs') }}</th><td>{{ job.source_lang }} → {{ job.target_lang }}</td></tr>
              <tr><th>{{ t('customer') }}</th><td>{{ job.user_name }} {{ job.user_surname }}</td></tr>
              <tr><th>{{ t('translationType') }}</th><td>{{ job.translation_type || '-' }}</td></tr>
              <tr><th>{{ t('specialization') }}</th><td>{{ job.specialization || '-' }}</td></tr>
              <tr v-if="job.source_lang === 'Osmanlıca'">
                <th>{{ t('hatType') }}</th>
                <td>{{ job.hat_tur_name || '-' }}</td>
              </tr>
              <tr><th>{{ t('deliveryDate') }}</th><td>{{ formatDate(job.delivery_date) }}</td></tr>
            </tbody>
          </table>

          <form @submit.prevent="submitOffer(job.id)" class="inline-form">
            <input type="number" v-model="offers[job.id].price" :placeholder="t('price')" required />
            <input type="date" v-model="offers[job.id].delivery_date" :min="today" required />
            <button class="btn small">{{ t('submitOffer') }}</button>
          </form>
        </div>
      </div>
    </div>

    <!-- Sayfalama -->
    <div class="pagination">
      <button @click="prevPage" :disabled="page === 1">{{ t('back') }}</button>
      <span>{{ t('page') }} {{ page }}</span>
      <button @click="nextPage" :disabled="!hasNextPage">{{ t('next') }}</button>
    </div>
  </div>
  <!-- Mobil kart görünüm -->
<div class="mobile-job-cards">
  <div
    class="mobile-card"
    v-for="job in paginatedJobs"
    :key="'card-' + job.id"
    @click="toggleOffer(job.id)"
  >
    <p><strong>{{ t('jobCode') }}:</strong> {{ formatJobCode(job.id) }}</p>
    <p>
      <strong>{{ t('file') }}:</strong>
      <a
        :href="`${API_URL}/download/${job.file_name}`"
        target="_blank"
        rel="noopener noreferrer"
        @click.stop
      >
        {{ getOriginalFileName(job.file_name) }}
      </a>
    </p>
    <p><strong>{{ t('langs') }}:</strong> {{ job.source_lang }} → {{ job.target_lang }}</p>
    <p><strong>{{ t('specialization') }}:</strong> {{ job.specialization || '-' }}</p>
    <p><strong>{{ t('customer') }}:</strong> {{ job.user_name }} {{ job.user_surname }}</p>
    <p v-if="job.source_lang === 'Osmanlıca'">
      <strong>{{ t('hatType') }}:</strong> {{ job.hat_tur_name || '-' }}
    </p>
    <p><strong>{{ t('deliveryDate') }}:</strong> {{ formatDate(job.delivery_date) }}</p>
    <button class="btn small">{{ t('offer') }}</button>
  </div>
</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getToken } from '@/utils/auth'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
// ✅ API URL (env üzerinden alınır)
const API_URL = process.env.VUE_APP_API_URL

// ✅ Tanımlanan reaktif veriler
const jobs = ref([])
const offers = ref({})
const offerVisible = ref({})
const search = ref('')
const minPrice = ref('')
const maxDeliveryDate = ref('')
const sortOption = ref('')
const page = ref(1)
const perPage = 10
const today = new Date().toISOString().split('T')[0]
const langFilter = ref('')
const sortKey = ref('')
const sortDirection = ref(1)
const hatTurleri = ref([])
const selectedHat = ref('')
const osmanlicaDilId = ref(null)
const specializationFilter = ref('')

// ✅ Açık işleri getir
async function fetchJobs() {
  const res = await fetch(`${API_URL}/translator/open-jobs`, {
    headers: { Authorization: `Bearer ${getToken()}` }
  })
  const data = await res.json()
  jobs.value = data
  data.forEach(job => {
    offers.value[job.id] = { price: '', delivery_date: '' }
    offerVisible.value[job.id] = false
  })
}

// ✅ Osmanlıca diline ait hat türlerini getir
async function fetchOsmanlicaHatTurleri() {
  const res = await fetch(`${API_URL}/user/languages`, {
    headers: { Authorization: `Bearer ${getToken()}` }
  })

  if (res.ok) {
    const langs = await res.json()
    const osmanlica = langs.find(l => l.name === 'Osmanlıca')
    if (osmanlica) {
      osmanlicaDilId.value = osmanlica.id
      const hatRes = await fetch(`${API_URL}/user/hat-turleri/${osmanlica.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      })
      if (hatRes.ok) {
        hatTurleri.value = await hatRes.json()
      }
    }
  }
}

// ✅ Popup aç/kapat
function toggleOffer(id) {
  offerVisible.value[id] = !offerVisible.value[id]
}

// ✅ Tabloda sıralama
function sortBy(key) {
  if (sortKey.value === key) {
    sortDirection.value *= -1
  } else {
    sortKey.value = key
    sortDirection.value = 1
  }
}

// ✅ Teklif gönderme
async function submitOffer(id) {
  const offer = offers.value[id]
  if (!offer.price || !offer.delivery_date) {
    return alert('Tüm alanlar zorunludur.')
  }

  const res = await fetch(`${API_URL}/translator/offer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`
    },
    body: JSON.stringify({
      job_id: id,
      price: offer.price,
      delivery_date: offer.delivery_date
    })
  })

  if (res.ok) {
    alert('Teklif gönderildi.')
    fetchJobs()
  } else {
    alert('Hata oluştu.')
  }
}

// ✅ Filtrelenmiş işler listesi
const filteredJobs = computed(() => {
  let list = jobs.value.filter(job => {
    const textMatch =
      (job.description || '').toLowerCase().includes(search.value.toLowerCase()) ||
      (job.user_name || '').toLowerCase().includes(search.value.toLowerCase()) ||
      (job.user_surname || '').toLowerCase().includes(search.value.toLowerCase())

    const langMatch = !langFilter.value || (
      (job.source_lang || '').toLowerCase().includes(langFilter.value.toLowerCase()) ||
      (job.target_lang || '').toLowerCase().includes(langFilter.value.toLowerCase())
    )
     const specializationMatch = !specializationFilter.value || 
      (job.specialization || '').toLowerCase().includes(specializationFilter.value.toLowerCase())


    const dateMatch = !maxDeliveryDate.value || job.delivery_date <= maxDeliveryDate.value
    const hatMatch = !selectedHat.value || job.hat_tur_name === selectedHat.value

    return textMatch && langMatch && dateMatch && hatMatch && specializationMatch
  })

  // Sıralama seçeneğine göre sırala
  if (sortOption.value === 'filename-asc') {
    list.sort((a, b) => a.file_name.localeCompare(b.file_name))
  } else if (sortOption.value === 'filename-desc') {
    list.sort((a, b) => b.file_name.localeCompare(a.file_name))
  } else if (sortOption.value === 'price-asc') {
    list.sort((a, b) => (offers.value[a.id]?.price || 0) - (offers.value[b.id]?.price || 0))
  } else if (sortOption.value === 'price-desc') {
    list.sort((a, b) => (offers.value[b.id]?.price || 0) - (offers.value[a.id]?.price || 0))
  } else if (sortOption.value === 'delivery-nearest') {
    list.sort((a, b) => new Date(a.delivery_date) - new Date(b.delivery_date))
  }

  // Kolon başlığına göre sıralama
  if (sortKey.value) {
    list.sort((a, b) => {
      const valA = a[sortKey.value] || ''
      const valB = b[sortKey.value] || ''
      if (valA < valB) return -1 * sortDirection.value
      if (valA > valB) return 1 * sortDirection.value
      return 0
    })
  }

  return list
})

// ✅ Sayfalama işlemleri
const paginatedJobs = computed(() => {
  const start = (page.value - 1) * perPage
  return filteredJobs.value.slice(start, start + perPage)
})

const hasNextPage = computed(() => page.value * perPage < filteredJobs.value.length)

function nextPage() {
  if (hasNextPage.value) page.value++
}

function prevPage() {
  if (page.value > 1) page.value--
}

// ✅ Yardımcı fonksiyonlar
function formatJobCode(id) {
  return 'JOB-' + String(id).padStart(6, '0')
}

function getOriginalFileName(fileName) {
  if (!fileName) return '-'
  return fileName.replace(/^\d+-/, '') // baştaki ID'yi temizle
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return new Date(dateStr).toISOString().split('T')[0]
}

function isSoon(dateStr) {
  if (!dateStr) return false
  const today = new Date()
  const target = new Date(dateStr)
  const diff = (target - today) / (1000 * 60 * 60 * 24)
  return diff >= 0 && diff <= 2 // 2 gün içinde teslimse vurgula
}

// ✅ İlk çalıştırıldığında verileri çek
onMounted(() => {
  fetchJobs()
  fetchOsmanlicaHatTurleri()
})
</script>


<style scoped>
/* ✅ Başlık */
.section-title {
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  text-align: center;
}

/* ✅ Filtre kutuları */
.filters-side {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
  justify-content: center;
}

.filters-side input,
.filters-side select {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  min-width: 180px;
  font-size: 14px;
}

/* ✅ Tablo */
.job-table {
  width: 100%;
  border-collapse: collapse;
  overflow-x: auto;
  display: block;
}

.job-table th,
.job-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #ddd;
  text-align: left;
  white-space: nowrap;
  font-size: 14px;
}

.job-table th {
  background-color: #f0f4f8;
  font-weight: 600;
}

.job-table tbody tr:hover {
  background-color: #f6faff;
}

/* ✅ Öne çıkan işler */
.highlight {
  background-color: #fff9db;
}

/* ✅ Butonlar */
.btn.small {
  padding: 6px 12px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.btn.small:hover {
  background: #2980b9;
}

/* ✅ Sayfalama */
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 25px;
  gap: 12px;
}

.pagination button {
  padding: 6px 14px;
  border: none;
  background-color: #3498db;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.pagination button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

/* ✅ Popup */
.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.popup-box {
  background: white;
  padding: 25px;
  border-radius: 10px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.3s ease;
}

/* ✅ Popup içindeki tablo */
.popup-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 15px;
}

.popup-table th,
.popup-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
  font-size: 14px;
}

.popup-table th {
  background-color: #f7f9fc;
  font-weight: 600;
  width: 40%;
}

/* ✅ Form */
.inline-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.inline-form input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
}

/* ✅ Sıralanabilir kolonlar */
.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.sortable:hover {
  background-color: #f0f4ff;
}

.sortable span {
  font-size: 0.8em;
  color: #555;
  margin-left: 5px;
}

/* ✅ Animasyon */
@keyframes fadeIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ✅ Mobil uyum */
@media (max-width: 600px) {
  .filters-side {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .job-table th,
  .job-table td,
  .popup-table th,
  .popup-table td {
    font-size: 13px;
    padding: 8px;
  }

  .btn.small {
    font-size: 13px;
    padding: 6px 10px;
  }

  .popup-box {
    padding: 20px;
  }

  .inline-form input {
    font-size: 13px;
  }
}
/* Mobil kart görünüm: yalnızca mobilde göster */
.mobile-job-cards {
  display: none;
}

/* Sadece mobilde göster */
@media (max-width: 768px) {
  .job-table {
    display: none;
  }

  .mobile-job-cards {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .mobile-card {
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 14px;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    font-size: 14px;
  }

  .mobile-card p {
    margin: 6px 0;
    word-break: break-word;
  }

  .mobile-card a {
    color: #3498db;
    text-decoration: underline;
  }
}

</style>